import { useEffect, useMemo, useRef, useState } from 'react';
import 'katex/dist/katex.min.css';
import { ResponsiveFractalCanvas } from './ResponsiveFractalCanvas';
import { ViewportFractalCanvas } from './ViewportFractalCanvas';
import { JuliaParameterExplorer } from './JuliaParameterExplorer';
import { OrbitTrajectoryExplorer } from './OrbitTrajectoryExplorer';
import { InverseIterationExplorer } from './InverseIterationExplorer';
import { JuliaTrajectorySubsection, SelfSimilarZoomSubsection } from './JuliaAnimationExplorers';
import {
  DEFAULT_JULIA_C,
  deepZoomPresets,
  overviewPresets,
  parameterMorphPresets,
  selfSimilarZoomPresets,
} from './fractalPresets';
import {
  DEFAULT_PALETTE_THEME,
  EXPLORER_PALETTE,
  PALETTE_THEMES,
  withPaletteTheme,
} from './fractalPalette';
import { codeSnippets, equations } from './staticContent';
import { renderedCode, renderedEquations } from './staticContent.generated';

export const REFERENCES = [
  { href: 'https://en.wikipedia.org/wiki/Julia_set', label: 'Wikipedia \u2014 Julia set' },
  { href: 'https://mathworld.wolfram.com/JuliaSet.html', label: 'Wolfram MathWorld \u2014 Julia Set' },
];

export const PAGE_SECTIONS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'world-overview', label: 'World overview' },
  { id: 'equation', label: 'Fractal equation' },
  {
    id: 'substructures',
    label: 'Sub-structures',
    subsections: [
      { id: 'substructures-overview', label: 'Overview' },
      { id: 'substructures-deep-zoom', label: 'Deep zoom' },
      { id: 'substructures-parameter-morphs', label: 'Parameter morphs' },
      { id: 'substructures-self-similar-zoom', label: 'Self-similar zoom' },
    ],
  },
  { id: 'coloring', label: 'Fractal coloring' },
  { id: 'about', label: 'About' },
];

// KaTeX and highlight.js run in scripts/generate-static-content.mjs, so the
// article ships as pre-rendered HTML and neither library reaches the browser.
const Equation = ({ name }) => {
  const html = renderedEquations[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered equation "${name}". Run \`yarn generate:content\`.`);
    }
    return <div className="equation" data-nosnippet="">{equations[name] ?? name}</div>;
  }
  return <div className="equation" data-nosnippet="" dangerouslySetInnerHTML={{ __html: html }} />;
};

const CodeBlock = ({ name }) => {
  const html = renderedCode[name];
  if (html === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Missing pre-rendered code snippet "${name}". Run \`yarn generate:content\`.`);
    }
    return <pre data-nosnippet=""><code className="language-javascript hljs">{codeSnippets[name] ?? name}</code></pre>;
  }
  return (
    <pre data-nosnippet="">
      <code
        className="language-javascript hljs"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </pre>
  );
};

const PaletteThemeControl = ({ paletteTheme, onSelectTheme, compact = false }) => (
  <div
    className={`palette-switcher${compact ? ' palette-switcher-compact' : ''}`}
    role="group"
    aria-label="Fractal color theme"
  >
    <span className="palette-switcher-label">Theme</span>
    <div className="palette-switcher-options">
      {Object.values(PALETTE_THEMES).map(({ id, label }) => {
        const isActive = paletteTheme === id;
        return (
          <button
            key={id}
            type="button"
            className={`palette-switcher-button${isActive ? ' active' : ''}`}
            data-theme-option={id}
            aria-label={label}
            aria-pressed={isActive}
            title={label}
            onClick={() => onSelectTheme(id)}
          >
            <span className={`palette-swatch palette-swatch-${id}`} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  </div>
);

const FractalSubsection = ({ entry, preferredRenderer, paletteTheme }) => {
  const { section, headingId, title, description, preset } = entry;
  const isDeepCameraPreset = Boolean(preset.camera);
  const fallbackSrc = entry.fallbackSrc ?? preset.fallbackSrc ?? `./images/${section}.webp`;
  const fallbackAlt = entry.fallbackAlt ?? preset.fallbackAlt ?? `${title} — ${description}`;
  const themedPalette = useMemo(
    () => withPaletteTheme(preset.palette, paletteTheme),
    [preset.palette, paletteTheme],
  );

  return (
    <section
      id={section}
      className="subsection fractal-subsection"
      data-section={section}
      aria-labelledby={headingId}
    >
      <h4 id={headingId}>{title}</h4>
      <p>{description}</p>
      <ResponsiveFractalCanvas
        {...preset}
        showControls
        deferUntilVisible={isDeepCameraPreset}
        palette={themedPalette}
        preferredRenderer={preferredRenderer}
        fallbackSrc={fallbackSrc}
        fallbackAlt={fallbackAlt}
      />
    </section>
  );
};

const SidebarNav = ({ paletteTheme, onSelectTheme }) => {
  const [activeSection, setActiveSection] = useState(PAGE_SECTIONS[0].id);

  useEffect(() => {
    let scrollFrame = null;
    const selectSection = nextId => setActiveSection(prev => (prev === nextId ? prev : nextId));

    const updateSidebarState = () => {
      scrollFrame = null;
      if (document.body?.classList.contains('fullscreen-active')) return;

      const rects = PAGE_SECTIONS.map(section => {
        const el = document.getElementById(section.id);
        return el ? { section, rect: el.getBoundingClientRect() } : null;
      }).filter(Boolean);

      if (rects.length === 0) return;

      const hasLayout = rects.some(({ rect }) =>
        rect.top !== 0 || rect.bottom !== 0 || rect.height !== 0
      );
      if (!hasLayout) return;

      const viewportHeight = window.innerHeight || 800;
      const docHeight = document.documentElement?.scrollHeight || 0;
      if (window.scrollY > 0 && docHeight > viewportHeight &&
          window.scrollY + viewportHeight >= docHeight - 24) {
        selectSection(PAGE_SECTIONS[PAGE_SECTIONS.length - 1].id);
        return;
      }

      const threshold = Math.max(120, viewportHeight * 0.28);
      let currentSection = PAGE_SECTIONS[0];
      for (const { section, rect } of rects) {
        if (rect.top <= threshold) {
          currentSection = section;
        }
      }

      if (currentSection.subsections?.length) {
        let currentSubId = currentSection.subsections[0].id;
        for (const sub of currentSection.subsections) {
          const subEl = document.getElementById(sub.id);
          if (!subEl) continue;
          const subRect = subEl.getBoundingClientRect();
          const subHasLayout = subRect.top !== 0 || subRect.bottom !== 0 || subRect.height !== 0;
          if (subHasLayout && subRect.top <= threshold) {
            currentSubId = sub.id;
          }
        }
        selectSection(currentSubId);
        return;
      }

      selectSection(currentSection.id);
    };

    const handleScrollOrResize = () => {
      if (globalThis.IS_REACT_ACT_ENVIRONMENT || typeof requestAnimationFrame !== 'function') {
        updateSidebarState();
        return;
      }
      if (scrollFrame === null) {
        scrollFrame = requestAnimationFrame(updateSidebarState);
      }
    };

    updateSidebarState();
    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      if (scrollFrame !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(scrollFrame);
      }
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, []);

  return (
    <nav
      className="sidebar-nav"
      aria-label="Page sections"
      data-nosnippet=""
    >
      <ul className="sidebar-nav-list">
        {PAGE_SECTIONS.map(({ id, label, subsections }) => {
          const hasActiveChild = Boolean(subsections?.some(sub => sub.id === activeSection));
          const isActive = activeSection === id;
          return (
            <li key={id} className="sidebar-nav-item">
              <a
                href={`#${id}`}
                className={`sidebar-nav-link${isActive ? ' active' : ''}${hasActiveChild ? ' active-parent' : ''}`}
                aria-current={isActive ? 'location' : undefined}
                onClick={() => setActiveSection(subsections?.length ? subsections[0].id : id)}
              >
                {label}
              </a>
              {subsections?.length > 0 && (
                <ul className="sidebar-nav-sublist">
                  {subsections.map(sub => {
                    const isSubActive = activeSection === sub.id;
                    return (
                      <li key={sub.id} className="sidebar-nav-subitem">
                        <a
                          href={`#${sub.id}`}
                          className={`sidebar-nav-link sidebar-nav-sublink${isSubActive ? ' active' : ''}`}
                          aria-current={isSubActive ? 'location' : undefined}
                          onClick={() => setActiveSection(sub.id)}
                        >
                          {sub.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <PaletteThemeControl
        paletteTheme={paletteTheme}
        onSelectTheme={onSelectTheme}
        compact
      />
    </nav>
  );
};

const JULIA_HERO_PALETTE = { ...EXPLORER_PALETTE, juliaC: DEFAULT_JULIA_C };
const JULIA_BASIN_C = Object.freeze([-0.087593732, 0.65509028]);
const JULIA_BASIN_PALETTE = { ...EXPLORER_PALETTE, juliaC: JULIA_BASIN_C };
const JULIA_FILIGREE_C = Object.freeze([-0.7269, 0.1889]);
const ITERATION_PALETTE = { ...EXPLORER_PALETTE, useMu: false, juliaC: DEFAULT_JULIA_C };

const App = () => {
  const preferredRenderer = 'webgl';
  const [paletteTheme, setPaletteTheme] = useState(DEFAULT_PALETTE_THEME);
  const [overviewJuliaC, setOverviewJuliaC] = useState(() => [...JULIA_FILIGREE_C]);
  const initialThemeMountRef = useRef(true);
  const heroPalette = useMemo(
    () => withPaletteTheme(JULIA_HERO_PALETTE, paletteTheme),
    [paletteTheme],
  );
  const overviewCustomPalette = useMemo(
    () => withPaletteTheme({ ...EXPLORER_PALETTE, juliaC: overviewJuliaC }, paletteTheme),
    [paletteTheme, overviewJuliaC],
  );
  const basinPalette = useMemo(
    () => withPaletteTheme(JULIA_BASIN_PALETTE, paletteTheme),
    [paletteTheme],
  );
  const iterationPalette = useMemo(
    () => withPaletteTheme(ITERATION_PALETTE, paletteTheme),
    [paletteTheme],
  );

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.paletteTheme = paletteTheme;
    }
    if (initialThemeMountRef.current) {
      initialThemeMountRef.current = false;
      return;
    }
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    const viewportHeight = window.innerHeight || 800;
    const renderEvent = new CustomEvent('render');
    for (const canvas of document.getElementsByTagName('canvas')) {
      const rect = canvas.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top <= viewportHeight) {
        canvas.dispatchEvent(renderEvent);
      }
    }
  }, [paletteTheme, overviewJuliaC]);

  return <>
  <header>
    <h1>The Julia Set</h1>
  </header>

  <SidebarNav
    paletteTheme={paletteTheme}
    onSelectTheme={setPaletteTheme}
  />

  <section id="intro" className="section" data-section="intro">
  <ResponsiveFractalCanvas
    width={1600}
    height={1600}
    fullWidth
    xRange={[-1.6, 1.6]}
    yRange={[-1.6, 1.6]}
    juliaC={DEFAULT_JULIA_C}
    palette={heroPalette}
    preferredRenderer={preferredRenderer}
    fallbackSrc="./images/julia-intro.webp"
    fallbackAlt="The dragon Julia set (c = -0.8 + 0.156i) in the complex plane with smooth plum, amethyst, champagne, and rose-gold escape-time coloring"
    fetchPriority="high"
  />
  <div className="container">
    <PaletteThemeControl
      paletteTheme={paletteTheme}
      onSelectTheme={setPaletteTheme}
    />
    <p>
      If we fix a complex constant c and plot the escape times for every
      starting point z<sub>0</sub> in the complex plane, each choice of c
      unfolds its own self-similar fractal world known as a Julia set.
    </p>
    <Equation name="julia-with-i" />
  </div>
  </section>

  <section id="world-overview" className="section" data-section="world-overview" aria-labelledby="world-overview-heading">
  <div className="container">
    <h2 id="world-overview-heading">World overview</h2>
    <p>
      Every Julia set has 180&deg; rotational symmetry around the center. Because
      squaring a number erases its sign, any starting point z<sub>0</sub> and its
      opposite &minus;z<sub>0</sub> land on the exact same value after the first
      step, giving every spiral arm a matching twin across the origin.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={600}
      xRange={[-1.6, 1.6]}
      yRange={[-1.2, 1.2]}
      juliaC={JULIA_BASIN_C}
      palette={basinPalette}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/world-symmetry-overview.webp"
      fallbackAlt="180-degree rotational symmetry of the three-branched rabbit rosette Julia set (c = -0.0876 + 0.6551i) across -1.6 to 1.6"
    />

    <p>
      While the Mandelbrot set is a single map, every complex number c produces
      its own Julia set. When c comes from inside the Mandelbrot set, the Julia
      set holds together as one connected shape; when c moves outside the
      boundary, the shape breaks apart into a scattered cloud of isolated points
      known as Fatou dust.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={600}
      xRange={[-1.6, 1.6]}
      yRange={[-1.2, 1.2]}
      juliaC={overviewJuliaC}
      palette={overviewCustomPalette}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/world-escape-radius.webp"
      fallbackAlt="Disconnected filigree Julia set (c = -0.7269 + 0.1889i) where interior basins shatter into logarithmic spiral Fatou dust"
    />
    <JuliaParameterExplorer paletteTheme={paletteTheme} onApplyParameter={setOverviewJuliaC} />
  </div>
  </section>

  <section id="equation" className="section" data-section="equation" aria-labelledby="equation-heading">
  <div className="container">
    <h2 id="equation-heading">Julia set equation</h2>

    <p>The equation using complex numbers is:</p>
    <Equation name="julia-iteration" />

    <p>Each pixel supplies the initial complex coordinate z<sub>0</sub>:</p>
    <Equation name="complex-number" />

    <p>While the complex parameter c stays fixed across the entire image:</p>
    <Equation name="complex-parameter" />

    <p>If we expand the complex squaring, the iteration reduces to:</p>
    <Equation name="real-part-iteration" />
    <Equation name="imaginary-part-iteration" />

    <section>
      <p>
        A common way of exploring Julia sets is by putting each pixel&apos;s starting (x<sub>0</sub>, y<sub>0</sub>)
        coordinates into the recurrence for a fixed c = (c<sub>x</sub>, c<sub>y</sub>) and making note of whether the series:
      </p>
      <ul>
        <li>diverges to infinity (the Fatou escaping basin) or stays within a bounded limit (the filled Julia set)</li>
        <li>if the series diverges, we make note of:
          <ul>
            <li>the number of iterations until reaching the escape radius</li>
            <li>the distance from zero (0, 0) during the escape</li>
          </ul>
        </li>
      </ul>
      <OrbitTrajectoryExplorer juliaC={DEFAULT_JULIA_C} paletteTheme={paletteTheme} />
    </section>
  </div>
  </section>

  <section id="substructures" className="section" data-section="substructures" aria-labelledby="substructures-heading">
  <div className="container">
    <h2 id="substructures-heading">Sub-structures</h2>
    <p>
      Across the complex plane, each parameter c shapes its Julia set into
      spirals, basilicas, Siegel disks, dendrites, and Cantor galaxies.
      Zooming toward repelling periodic points and their preimages uncovers the
      same global motif repeating at every scale.
    </p>

    <div id="substructures-overview" className="substructures-subsection">
      <h3 className="substructures-subsection-title">Overview</h3>
      <p className="substructures-subsection-intro">
        At shallow magnifications, every pixel runs its own independent
        escape-time loop inside a WebGL fragment shader using native 32-bit
        GPU floats, seeding <code>z<sub>0</sub></code> directly from the pixel
        coordinate and iterating with the fixed complex constant{' '}
        <code>c</code>.
      </p>
      {overviewPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} paletteTheme={paletteTheme} />
      ))}
    </div>
  </div>

  <div
    id="substructures-deep-zoom"
    className="substructures-subsection substructures-subsection-deep"
    data-section="deep-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Deep zoom</h3>
      <p className="substructures-subsection-intro">
        Once the viewport narrows past 10<sup>-3</sup>, subpixel spacing
        approaches the 10<sup>-7</sup> rounding step of single-precision{' '}
        <code>float32</code> shaders. These views step up
        to compensated double-single (<code>dsAdd</code> / <code>dsMul</code>)
        Dekker-split shaders and 53-bit significand <code>float64</code>{' '}
        workers to keep filaments razor-sharp down to the 10<sup>-15</sup> IEEE
        754 precision floor.
      </p>
      {deepZoomPresets.map(entry => (
        <FractalSubsection key={entry.section} entry={entry} preferredRenderer={preferredRenderer} paletteTheme={paletteTheme} />
      ))}
    </div>
  </div>

  <div
    id="substructures-parameter-morphs"
    className="substructures-subsection substructures-subsection-parameter-morphs"
    data-section="parameter-morphs"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Parameter morphs</h3>
      <p className="substructures-subsection-intro">
        Because a single Julia set fixes <code>c</code> across the entire
        complex plane, its richest topological transitions happen when{' '}
        <code>c(t)</code> travels along a path in the Mandelbrot parameter
        plane. Sweeping <code>c(t)</code> around the main cardioid, across
        period-doubling necks, onto Misiurewicz filaments, or past the
        connectedness boundary drives the filled Julia set through continuous
        bifurcations and Cantor dust transitions at interactive frame rates.
      </p>
      {parameterMorphPresets.map(entry => (
        <JuliaTrajectorySubsection key={entry.section} entry={entry} paletteTheme={paletteTheme} />
      ))}
    </div>
  </div>

  <div
    id="substructures-self-similar-zoom"
    className="substructures-subsection substructures-subsection-self-similar-zoom"
    data-section="self-similar-zoom"
  >
    <div className="container">
      <h3 className="substructures-subsection-title">Self-similar zoom</h3>
      <p className="substructures-subsection-intro">
        By Koenigs&apos; Linearization Theorem, iterating <code>z&sup2; + c</code>{' '}
        near a repelling fixed point <code>&alpha;<sub>c</sub></code> with
        multiplier <code>&lambda; = 2&alpha;<sub>c</sub></code> (
        <code>|&lambda;| &gt; 1</code>) is conformally conjugate to
        multiplying by <code>&lambda;</code>. Scaling
        the viewport by <code>|&lambda;|<sup>&minus;mt</sup></code>,
        counter-rotating by <code>&minus;t&middot;arg(&lambda;<sup>m</sup>)</code>,
        and shifting smooth escape counts by <code>&minus;mt</code> loops a
        single shallow octave seamlessly without needing arbitrary-precision
        arithmetic.
      </p>
      {selfSimilarZoomPresets.map(entry => (
        <SelfSimilarZoomSubsection key={entry.section} entry={entry} paletteTheme={paletteTheme} />
      ))}
    </div>
  </div>
  </section>

  <section id="coloring" className="section" data-section="coloring" aria-labelledby="coloring-heading">
  <div className="container">
    <h2 id="coloring-heading">Fractal coloring</h2>

    <p>
      Given a starting point (x<sub>0</sub>, y<sub>0</sub>) and a fixed
      parameter (c<sub>x</sub>, c<sub>y</sub>), escape-time coloring records
      how many iterations the orbit takes to cross the escape circle
      |z| &gt; 2 along with the complex coordinate where it escapes.
    </p>
    <CodeBlock name="iterate-until-escape" />

    <p>
      To keep high iteration counts near the Julia boundary from bunching into
      noisy stripes, <code>palettePhase</code> compresses the escape count with
      a square-root curve before sampling a 512-entry periodic cubic spline
      interpolated in Oklab color space.
    </p>
    <CodeBlock name="iteration-color-palette" />

    <p>
      Sweeping across the viewport grid maps each pixel to its starting
      coordinate (x<sub>0</sub>, y<sub>0</sub>), runs the recurrence for the
      fixed parameter (c<sub>x</sub>, c<sub>y</sub>), and writes the sampled
      RGBA color for every escaping orbit while leaving bounded interior points
      dark.
    </p>

    <CodeBlock name="draw-fractal-canvas" />

    <p>
      Here&apos;s the central spiral of the dragon Julia set
      (c = &minus;0.8 + 0.156i) colored from integer iteration counts alone,
      where each discrete escape step forms a visible contour band.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={600}
      showControls
      xRange={[-0.48, 0.48]}
      yRange={[-0.36, 0.36]}
      juliaC={DEFAULT_JULIA_C}
      palette={iterationPalette}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/dragon-iteration-coloring.webp"
      fallbackAlt="Central spiral of the dragon Julia set colored using discrete integer iteration counts, showing contour bands"
    />
    <p>
      Running three additional iterations after crossing |z| &gt; 2 and
      subtracting the doubly logarithmic escape potential turns the integer
      step count into a continuous escape time <code>mu</code>.
    </p>
    <CodeBlock name="escape-time-coloring" />
    <p>
      Feeding <code>mu</code> into the same repeating{' '}
      {paletteTheme === 'celadon'
        ? 'jade, celadon, porcelain, and cinnabar'
        : 'plum, amethyst, champagne, and rose-gold'}{' '}
      Oklab spline removes the stepped contour seams across the spiral arms.
    </p>
    <ResponsiveFractalCanvas
      width={800}
      height={600}
      showControls
      xRange={[-0.48, 0.48]}
      yRange={[-0.36, 0.36]}
      juliaC={DEFAULT_JULIA_C}
      palette={heroPalette}
      preferredRenderer={preferredRenderer}
      fallbackSrc="./images/dragon-smooth-coloring.webp"
      fallbackAlt="Central spiral of the dragon Julia set colored with continuous logarithmic escape-time smoothing"
    />
    <p>
      Inverse iteration and orbit-density methods render Julia sets by plotting
      either the backward preimages of repelling periodic points or the visit
      frequency of escaping trajectories across the viewport.
    </p>
    <InverseIterationExplorer paletteTheme={paletteTheme} />
  </div>
  </section>

    <section id="about" className="section" data-section="about" aria-labelledby="about-heading">
      <div className="container">
        <h2 id="about-heading">About</h2>
        <p>
          All fractals on the page are interactive and rendered when
          they&apos;re in view. Every image can be expanded into full-screen
          mode using the button in its bottom-right corner. Full-screen views
          also display live complex-plane coordinates and let you undo camera
          steps or reset to the starting view.
        </p>
        <p>
          As you zoom deeper, each canvas automatically switches to more
          sophisticated rendering pipelines to stay fast and avoid pixelation.
          Shallow views run per-pixel escape loops in 32-bit WebGL fragment
          shaders.
        </p>
        <p>
          Zooming past 10<sup>-3</sup> switches to compensated double-single
          shaders and 64-bit workers. Beyond 10<sup>-14</sup>, the renderer
          switches to perturbation theory and arbitrary-precision decimal math
          so you can keep zooming without losing detail.
        </p>
        <p data-nosnippet="">Fractal image controls when hovering</p>
        <ul data-nosnippet="">
          <li>shift + scroll mouse wheel — zoom in/out</li>
          <li>shift + click and drag — draw a selection box to zoom in</li>
          <li>double-click (or shift + double-click) — zoom in/out at cursor</li>
          <li>+ / &minus; keys — zoom in/out</li>
          <li>pinch — zoom in/out</li>
          <li>click and drag — pan the view</li>
          <li>arrow keys (when focused or hovering) — pan the view in 10% steps</li>
          <li>two-finger swipe — pan the view on touchscreens</li>
          <li>one-finger swipe — scroll the page</li>
          <li>cmd-z / cmd-shift-z — undo or redo camera actions</li>
          <li>0 — reset view to its initial position</li>
        </ul>
      </div>
    </section>

    <footer>
      <div className="container">
        <h2 id="references-heading">References</h2>
        <ul>
          {REFERENCES.map(({ href, label }) => (
            <li key={href}><a href={href}>{label}</a></li>
          ))}
        </ul>
      </div>
    </footer>

    <ViewportFractalCanvas preferredRenderer={preferredRenderer} paletteTheme={paletteTheme} />
</>;
};

export default App;
